import { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import PrivacyPolicyStandalone from './PrivacyPolicyStandalone';

export default function CookieConsent() {
  const [showConsent, setShowConsent] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useEffect(() => {
    // Check if user has already accepted/declined cookies
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Show the popup after a short delay for better UX
      const timer = setTimeout(() => {
        setShowConsent(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShowConsent(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShowConsent(false);
  };

  if (!showConsent) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/30 pointer-events-auto animate-fade-in"
          onClick={handleDecline}
        />
        
        {/* Cookie Consent Box */}
        <div className="relative w-full max-w-4xl mx-4 mb-6 bg-white rounded-lg shadow-2xl pointer-events-auto transform transition-all duration-300 animate-slide-up">
          <div className="p-6 sm:p-8">
            {/* Close button */}
            <button
              onClick={handleDecline}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>

            <div className="flex items-start gap-4">
              {/* Cookie Icon */}
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Cookie className="text-emerald-700" size={24} />
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  We Value Your Privacy
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  We use cookies to enhance your browsing experience, provide personalized content, 
                  and analyze our traffic. Cookies help us improve our website and services. 
                  By clicking "Accept All", you consent to our use of cookies. You can manage your 
                  preferences or learn more in our{' '}
                  <button 
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-emerald-700 hover:text-emerald-800 underline font-medium cursor-pointer"
                  >
                    Privacy Policy
                  </button>.
                </p>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAccept}
                    className="px-6 py-3 bg-emerald-700 text-white font-semibold rounded-lg hover:bg-emerald-800 transition-colors duration-200 shadow-md hover:shadow-lg"
                  >
                    Accept All Cookies
                  </button>
                  <button
                    onClick={handleDecline}
                    className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes slide-up {
            from {
              opacity: 0;
              transform: translateY(100px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .animate-slide-up {
            animation: slide-up 0.4s ease-out;
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out;
          }
        `}</style>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 flex items-start justify-center p-4">
          <div 
            id="privacy-modal-container"
            className="relative bg-white rounded-lg max-w-4xl w-full my-8 shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <button
              onClick={() => setShowPrivacyModal(false)}
              className="sticky top-4 left-full ml-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10 transition-colors"
              aria-label="Close Privacy Policy"
            >
              <X size={24} />
            </button>
            <PrivacyPolicyStandalone onClose={() => setShowPrivacyModal(false)} />
          </div>
        </div>
      )}
    </>
  );
}